import React from "react";
import { Link } from "react-router-dom";
import { business, nav, social, googleReviewUrl, footerLinks } from "../data/siteData";
import SocialIcon from "./SocialIcon";
import { Phone, MessageSquare } from "lucide-react";

export default function Footer() {
  return (
    <footer>
      {/* nav columns */}
      <div className="bg-[#0c2a66] text-white">
        <div className="mr-container py-12">
          <ul className="grid grid-cols-2 gap-x-8 gap-y-7 sm:grid-cols-3 lg:grid-cols-4">
            {nav.map((item) => (
              <li key={item.label}>
                {item.to === "#" ? (
                  <span className="font-head text-[13px] font-extrabold uppercase tracking-[0.12em] text-white">
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className="font-head text-[13px] font-extrabold uppercase tracking-[0.12em] text-white transition-colors hover:text-[#9dbcff]"
                  >
                    {item.label}
                  </Link>
                )}
                {item.children && (
                  <ul className="mt-2 space-y-1.5">
                    {item.children.map((c) => (
                      <li key={c.label}>
                        {c.href ? (
                          <a
                            href={c.href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[13px] text-white/75 transition-colors hover:text-white"
                          >
                            {c.label}
                          </a>
                        ) : (
                          <Link
                            to={c.to}
                            className="text-[13px] text-white/75 transition-colors hover:text-white"
                          >
                            {c.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li data-testid="footer-payments-column">
              <span className="font-head text-[13px] font-extrabold uppercase tracking-[0.12em] text-white">
                Payments &amp; Reviews
              </span>
              <ul className="mt-2 space-y-1.5">
                {footerLinks.map((l) => (
                  <li key={l.label}>
                    {l.href ? (
                      <a
                        href={l.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[13px] text-white/75 transition-colors hover:text-white"
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="text-[13px] text-white/75 transition-colors hover:text-white"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
      </div>

      {/* white brand band with review links */}
      <div className="bg-white">
        <div className="mr-container flex flex-col items-center gap-6 py-9">
          <Link to="/">
            <img src={business.logo} alt="Mr. Resurface LLC" className="h-16 w-auto" />
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <a
              href={social[0].url}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border-2 border-[#244aa2] px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
            >
              Review Us On Facebook!
            </a>
            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border-2 border-[#244aa2] px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
            >
              Review Us On Google!
            </a>
            <a
              href={social[1].url}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border-2 border-[#244aa2] px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
            >
              Check Out Our Twitter
            </a>
            <a
              href={social[2].url}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border-2 border-[#244aa2] px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
            >
              BBB
            </a>
            <a
              href={social[3].url}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm border-2 border-[#244aa2] px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
            >
              Angies List
            </a>
          </div>
          <div className="flex flex-col gap-2.5 sm:flex-row">
            <Link
              to="/make-a-payment"
              className="border-2 border-[#a10000] px-7 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-[#a10000] transition-colors duration-200 hover:bg-[#a10000] hover:text-white"
            >
              Make a Payment
            </Link>
            <Link
              to="/contact-us"
              className="bg-[#244aa2] px-7 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-[#0c2a66]"
            >
              Contact Us For Estimate
            </Link>
            <a
              href={`sms:1${business.phoneDigits}`}
              className="flex items-center justify-center gap-2 bg-[#a10000] px-7 py-3 text-center text-[13px] font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-[#7d0000]"
            >
              <MessageSquare className="h-4 w-4" /> Text Pictures For Estimate
            </a>
          </div>
        </div>
      </div>

      {/* copyright */}
      <div className="bg-[#0c2a66] text-white">
        <div className="mr-container flex flex-col items-center gap-3 py-6 text-center text-[13px]">
          <div className="flex items-center gap-1.5">
            {social.map((s) => (
              <a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/40 transition-colors duration-200 hover:bg-white hover:text-[#0c2a66]"
              >
                <SocialIcon name={s.icon} />
              </a>
            ))}
          </div>
          <p>{business.copyright}</p>
          <p className="text-white/85">
            {business.address.line1},&nbsp; {business.address.line2},&nbsp;{" "}
            {business.address.city}, {business.address.state} {business.address.zip}&nbsp;&nbsp;
            <a href={`tel:${business.phoneRaw}`} className="inline-flex items-center gap-1 underline">
              <Phone className="h-3.5 w-3.5" />
              {business.phone}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
